import { useRoute } from "@react-navigation/native";
import {
  Divider,
  IndexPath,
  Select,
  SelectItem,
  Text,
} from "@ui-kitten/components";
import BaseCard from "components/BaseCard";
import Button from "components/Button";
import BuyFlowPaymentDetailCard from "components/BuyFlowPaymentDetailCard";
import Spacer from "components/Spacer";
import {
  setSelectedBillingInterval,
  setSelectedPlanPackage,
  setStep,
} from "features/slices/buyFlowSlice";
import { BillingInterval, PlanPackageDetails } from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import useIsRunningInBallpark from "hooks/useIsRunningInBallpark";
import React, { useEffect, useMemo, useState } from "react";
import { View } from "react-native";
import {
  billingIntervalDisplayValue,
  getPlanType,
} from "utils/planPackageUtils";
import PlanDescriptionComponent from "../../components/PlanDescriptionComponent";

interface StepOneProps {
  planPackages: PlanPackageDetails[];
}
const StepOne: React.FC<StepOneProps> = ({ planPackages }) => {
  const route = useRoute();

  const {
    selectedBillingInterval,
    signupCharge,
    planPackageUnavailable,
    selectedPlanPackage,
  } = useAppSelector((state) => state.buyFlow);
  const { account } = useAccount();
  const accountSubscriptions = account?.subscriptions || [];
  const { isRunningInBallpark } = useIsRunningInBallpark();
  const { tenantSubdomain } = useAppSelector((state) => state.tenant);
  const dispatch = useAppDispatch();
  const [billingIntervalIndex, setBillingIntervalIndex] = useState<IndexPath>(
    new IndexPath(0)
  );
  const [availableBillingIntervals, setAvailableBillingIntervals] = useState<
    BillingInterval[]
  >([]);

  const displayValue = selectedPlanPackage?.name || "";
  const planType = useMemo(
    () => (selectedPlanPackage ? getPlanType(selectedPlanPackage) : undefined),
    [selectedPlanPackage]
  );

  const billingIntervaldisplayValue = billingIntervalDisplayValue(
    selectedBillingInterval
  );

  const getSelectedIndex = () => {
    const index = planPackages.findIndex(
      (plan) => plan.id === selectedPlanPackage?.id
    );
    return new IndexPath(index);
  };

  // https://github.com/FanRally/fanrally-core/issues/571
  // When adding a new subscription to an existing account, all of the subscriptions of the same plan package must use the same billing interval!
  const getAvailableBillingIntervals = () => {
    const accountHasSelectedPlanPackage = accountSubscriptions.find(
      (sub) =>
        sub.planPackage.id === selectedPlanPackage?.id &&
        sub.status === "ACTIVE"
    );
    if (accountHasSelectedPlanPackage) {
      return [accountHasSelectedPlanPackage.billingInterval];
    }
    return selectedPlanPackage?.billingIntervals || [];
  };

  const shouldShowChildQuantityModifier = useMemo(() => {
    return (
      planType !== "BANK" &&
      signupCharge !== null &&
      route.name === "AddMembership"
    );
  }, [planType, signupCharge, route.name]);

  const initiateBillingInterval = () => {
    const billingIntervals = getAvailableBillingIntervals();
    if (billingIntervals.length === 1) {
      dispatch(setSelectedBillingInterval(billingIntervals[0]));
    }
    setAvailableBillingIntervals(billingIntervals);
  };

  useEffect(() => {
    if (!selectedPlanPackage) return;
    initiateBillingInterval();
  }, [selectedPlanPackage]);

  if (isRunningInBallpark) {
    return (
      <Text category="h5" style={{ textAlign: "center" }}>
        In order to add additional members to your account, please visit{" "}
        <Text category="h5" status="info">
          {tenantSubdomain as string}.go.fanrally.com
        </Text>
        , and login to your account to view available plans.
      </Text>
    );
  }
  if (planPackageUnavailable) {
    return (
      <Text category="h2" style={{ textAlign: "center" }}>
        No available plan packages
      </Text>
    );
  }
  if (planPackages.length === 0 || !selectedPlanPackage) {
    return (
      <Text category="h2" style={{ textAlign: "center" }}>
        loading...
      </Text>
    );
  }

  return (
    <View>
      <BaseCard>
        <Select
          value={displayValue}
          selectedIndex={getSelectedIndex()}
          onSelect={(index) => {
            dispatch(
              setSelectedPlanPackage(planPackages[(index as IndexPath).row])
            );
          }}
        >
          {planPackages.map((planPackage) => (
            <SelectItem key={planPackage.id} title={planPackage.name} />
          ))}
        </Select>
        <Spacer />

        <PlanDescriptionComponent
          description={selectedPlanPackage.description}
        />
      </BaseCard>

      <Spacer />

      {availableBillingIntervals.length > 1 && (
        <BaseCard title="Choose How You Pay">
          <Divider style={{ marginVertical: 15 }} />
          <Select
            value={billingIntervaldisplayValue}
            selectedIndex={billingIntervalIndex}
            onSelect={(index) => {
              setBillingIntervalIndex(index as IndexPath);
              dispatch(
                setSelectedBillingInterval(
                  availableBillingIntervals[(index as IndexPath).row]
                )
              );
            }}
          >
            {selectedPlanPackage?.billingIntervals.map((interval) => (
              <SelectItem
                key={interval}
                title={billingIntervalDisplayValue(interval)}
              />
            ))}
          </Select>
        </BaseCard>
      )}

      <Spacer />

      {signupCharge ? (
        <BuyFlowPaymentDetailCard
          planPackage={selectedPlanPackage}
          signupCharge={signupCharge}
        />
      ) : null}

      <Spacer margin={20} />

      <Button
        onPress={() =>
          setTimeout(() => {
            dispatch(setStep(2)); // to avoid Stripe elements not mounted bug
          }, 200)
        }
        testID="add-membership-step-one-button"
      >
        Continue
      </Button>
    </View>
  );
};

export default StepOne;
